/* eslint-disable no-return-assign, react/sort-comp, no-param-reassign, no-console, radix, array-callback-return, consistent-return */
import React, { Component, PropTypes } from 'react';
import ReactDOM from 'react-dom';

/**
 * Usage:
 *
 * `<FitText maxHeight={100}>text</FitText>`
 *
 * ```
 * <div style={{ maxHeight: '100px' }}>
 *   <p>
 *     <FitText>text</FitText>
 *   </p>
 * </div>
 * ```
 *
 * FitText shrinks (but does not grow) the font size of all nested children inside of it until the
 * height of the FitText component is <= the maxHeight. You can specify a maxHeight (in pixels)
 * explicitly via the `maxHeight` prop, or by setting the `max-height` CSS property of  any of its
 * parent elements (but not the FitText element itself). It is intended to replace the legacy
 * variable_type class.
 *
 * NOTE: You should never give the FitText component a fixed height, or else its height will not
 * shrink no matter how much the font sizes are reduced, and FitText will have no way of knowing
 * when it should stop reducing the font size.
 *
 * NOTE: The children inside of a FitText component should only be basic HTML elements and not React
 * components. If a React component inside of a FitText updates itself via setState/forceUpdate,
 * then componentWillUpdate/componentDidUpdate will not be called on the containing FitText, and
 * resizing of the font sizes will not be triggered.
 *
 * If FitText needs to wrap React components in the future, or if the ability to resize some pieces
 * of text but not others in the same container is needed, it might be a good idea to implement a
 * Context/Child pattern to explicity specify which components should have their text resized. This
 * might look something like:
 *
 * <FitTextContext maxHeight={100}>
 *   <p>Don't resize this.</p>
 *   <p><FitTextChild>Resize this.</FitTextChild></p>
 * </FitTextContext>
 *
 * FitTextChild components would tell their context to do font resizing when they update, and the
 * FitTextContext would resize all of the FitTextChild components inside of it together.
 */
export default class FitText extends Component {
  constructor() {
    super();

    // Save a reference to the container's DOM node so we can walk through its children and modify
    // their styles directly.
    this.saveContainerNode = ref => this.container = ReactDOM.findDOMNode(ref);
  }

  componentDidMount() {
    this.resize();
  }

  componentWillUpdate() {
    this.resetFontSizes();
  }

  componentDidUpdate() {
    this.resize();
  }

  /**
   * Before the child elements update, reset all of ther fontSize inline styles to whatever they
   * were before we started messing with the styles.
   *
   * NOTE: By the time resize() gets called by componentDidUpdate, the fontSize inline style will
   * not necessarily be the same value as it was set to here, because one of the child components
   * might have an updated style attribute.
   */
  resetFontSizes() {
    const elements = flattenAllElements(this.container);
    elements.forEach(element => {
      const originalFontSize = element.dataset[FitText.ORIGINAL_FONT_SIZE];
      if (originalFontSize) {
        element.style.fontSize = `${originalFontSize}px`;
      }
    });
  }

  /**
   * Shrink the font-size of the container and all of its nested children 1px at a time until
   * either:
   *
   * 1. The size of the container is less then its maxHeight.
   * 2. All of the elements have been shrunken to the minimumFontSize.
   */
  resize() {
    const maxHeight = this.getMaxHeight();

    // We can't fit the text if we don't know what dimensions it needs to fit inside of.
    if (process.env.NODE_ENV !== 'production' && !Number.isFinite(maxHeight)) {
      console.error('FitText aborting resizing because there was no maxHeight prop or ' +
        'max-height style on container element.');
      return;
    }

    // Get all nested elements as a flat array.
    const elements = flattenAllElements(this.container);

    // Package each element with its font size converted to a number so we can easily change it.
    //
    // Also, save what the font size was before we started messing with the styles.
    const children = elements.map(element => {
      const initialFontSize = parseInt(window.getComputedStyle(element).fontSize.replace(/px/));

      element.dataset[FitText.ORIGINAL_FONT_SIZE] = initialFontSize;

      return { element, fontSize: initialFontSize };
    });

    // Returns false if all of the elements are already at the minimum font size.
    const canShrink = () =>
      children.some(({ fontSize }) => fontSize > this.props.minFontSize);

    // Loop until the container fits the maxHeight.
    while (this.container.getBoundingClientRect().height > maxHeight && canShrink()) {
      children.map(child => {
        // For each child, reduce its font size if it's not already at the minimum size.
        if (child.fontSize > this.props.minFontSize) {
          child.fontSize -= 1;
          child.element.style.fontSize = `${child.fontSize}px`;
        }
      });
    }
  }

  /**
   * If the maxHeight prop is set, returns it.
   *
   * Otherwise, searches through all parent elements until it finds the first element with a
   * fixed max-height CSS property and returns it as an integer.
   */
  getMaxHeight() {
    // Return the maxHeight prop if set.
    if (this.props.maxHeight) {
      return this.props.maxHeight;
    }

    // Make sure that the container itself doesn't have a max-height, because that will cause
    // getBoundingClientRect() to be bound by that max-height, making it impossible to check if we
    // need to resize the text to fit inside the container.
    if (process.env.NODE_ENV !== 'production' &&
      Number.isFinite(parseInt(window.getComputedStyle(this.container).maxHeight.replace(/px/)))) {
      console.error('FitText aborting resizing because the container had a max-height set.');
      return undefined;
    }

    // Otherwise, search the chain of parent elements and return the first one with a fixed
    // max-height CSS property set.
    let element = this.container.parentElement;

    while (element) {
      // Get the max-height property.
      const computedStyle = window.getComputedStyle(element);
      const maxHeight = parseInt(computedStyle.maxHeight.replace(/px/));

      // Return the max-height if it is set.
      if (Number.isFinite(maxHeight)) {
        return maxHeight;
      }

      // Otherwise, try the parent element.
      element = element.parentElement;
    }

    // Just return undefined if we couldn't find any max-height property.
    return undefined;
  }

  render() {
    return (
      <span style={{ display: 'inline-block' }} ref={this.saveContainerNode}>
        {this.props.children}
      </span>
    );
  }
}

FitText.propTypes = {
  // The maximum container height in pixels.
  maxHeight: PropTypes.number,
  // TODO: Add support for setting maxWidth?
  // The minimum font size in pixels.
  minFontSize: PropTypes.number,
  // Only allow basic HTML elements in the entire tree of children, because if a child React
  // component updates itself via setState then the FitText component won't receive the
  // componentDidUpdate event and won't resize the fonts.
  children: (props, propName, componentName) => {
    const walkChildren = (children, callback) => {
      React.Children.forEach(children, child => {
        callback(child);

        if (child && child.props && child.props.children) {
          walkChildren(child.props.children, callback);
        }
      });
    };

    const throwIfNotBasicElement = (element) => {
      if (element && element.type && typeof element.type !== 'string') {
        throw new Error('FitText components should only contain basic HTML elements. If you must have ' +
          'React components as children, you should set an key property on the FitText component ' +
          'based on the text to force it to re-shrink the text when it changes.');
      }
    };

    try {
      walkChildren(props.children, throwIfNotBasicElement);
    } catch (error) {
      // Custom validators are supposed to return errors instead of throwing them, so catch the
      // error and then return it.
      return error;
    }
  },
};

FitText.defaultProps = {
  minFontSize: 11,
};

// Name of the data attribute used to save the original font size of each child element.
FitText.ORIGINAL_FONT_SIZE = '__fitTextOriginalFontSize';

/**
 * Given a DOM element returns a flattened array of all of it and its nested children.
 */
function flattenAllElements(parentElement) {
  const result = [parentElement];

  const walkChildren = children => {
    if (!children) {
      return;
    }

    for (let i = 0; i < children.length; i++) {
      // Don't add elements with the data-fit-text="no-resize".
      if (children[i].dataset.fitText !== 'no-resize') {
        result.push(children[i]);

        // Depth-first search through sub-children.
        walkChildren(children[i].children);
      }
    }
  };

  walkChildren(parentElement.children);

  return result;
}
